import { useEffect, useState } from 'react';
import { Box, FormControl, FormControlLabel, Paper, Switch, TextField, Typography } from '@mui/material';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import { t } from 'i18next';
import Swal from 'sweetalert2';
import { SCUIButton } from '../../../../atomic/atoms';
import { SCUIGeneralDrawerHeader } from '../../../../atomic/molecules';
import { OperatorCommand } from '../../../../services/command';
import { LUR } from '../listUserRoles.classes';

const UpdateUserComponent = ({ selectedUser, refresh, onClose, hideHeader = false }: any) => {
    const [formData, setFormData] = useState({
        email: selectedUser ? selectedUser.email : '',
        storeCode: selectedUser ? selectedUser.storeCode : '',
        operatorId: selectedUser ? selectedUser.operatorId : '',
        isActive: selectedUser ? selectedUser.isActive : false,
        applicationUserId: selectedUser ? selectedUser.applicationUserId : '',
    });

    const handleInputChange = (e: any) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const handleSwitchChange = (e: any) => {
        setFormData({
            ...formData,
            isActive: e.target.checked,
        });
    };

    const handleSubmitButton = async () => {
        try {
            const params: any = {};
            params.applicationUserId = selectedUser.applicationUserId;
            params.email = formData.email;
            params.storeCode = formData.storeCode;
            params.operatorId = formData.operatorId;
            params.isActive = formData.isActive;
            await OperatorCommand.UpdateApplicationUser(params).then(() => {
                Swal.fire({
                    icon: 'success',
                    title: t('general.success'),
                    text: t('general.userInfoUpdated'),
                    showConfirmButton: false,
                    timer: 1500,
                });
            });
        } catch (error) {
            console.log('Hata :>> ', error);
        } finally {
            refresh();
        }
    };

    useEffect(() => {
        if (selectedUser) {
            setFormData({
                email: selectedUser.email,
                storeCode: selectedUser.storeCode,
                operatorId: selectedUser.operatorId,
                isActive: selectedUser.isActive,
                applicationUserId: selectedUser.applicationUserId,
            });
        }
    }, [selectedUser]);

    const textFieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: '#FAFAFA',
            transition: 'all 0.15s ease',
            '&:hover': { backgroundColor: '#fff' },
            '&.Mui-focused': { backgroundColor: '#fff' },
        },
    };

    return (
        <Box
            className={LUR.updateRoot}
            data-application-user-id={selectedUser?.applicationUserId}
            data-user-ref={selectedUser?.userRef}
            sx={{ p: hideHeader ? 0 : 3 }}
        >
            {!hideHeader && (
                <Box className={LUR.updateHeaderBox} sx={{ mb: 2 }}>
                    <SCUIGeneralDrawerHeader header={t('general.userEdit')} onClose={onClose} />
                </Box>
            )}

            <Paper elevation={0} className={LUR.updateFormPaper} sx={hideHeader ? { p: 0, boxShadow: 'none' } : undefined}>
                {!hideHeader && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                        <EditRoundedIcon sx={{ color: '#7c3aed' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827' }}>
                            {t('authorizationManagement.editUser') as string}
                        </Typography>
                    </Box>
                )}

                <Box className={LUR.updateFieldEmail} sx={{ mb: 2.5 }}>
                    <FormControl fullWidth>
                        <TextField
                            className={LUR.inputUpdateEmail}
                            label={t('general.eMail') as string}
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            sx={textFieldSx}
                        />
                    </FormControl>
                </Box>

                <Box className={LUR.updateFieldStoreCode} sx={{ mb: 2.5 }}>
                    <FormControl fullWidth>
                        <TextField
                            className={LUR.inputUpdateStoreCode}
                            label={t('general.storeCode') as string}
                            name="storeCode"
                            value={formData.storeCode}
                            onChange={handleInputChange}
                            sx={textFieldSx}
                        />
                    </FormControl>
                </Box>

                <Box className={LUR.updateFieldOperatorId} sx={{ mb: 2.5 }}>
                    <FormControl fullWidth>
                        <TextField
                            className={LUR.inputUpdateOperatorId}
                            label={t('general.operatorId') as string}
                            name="operatorId"
                            value={formData.operatorId}
                            onChange={handleInputChange}
                            sx={textFieldSx}
                        />
                    </FormControl>
                </Box>

                <Box className={LUR.updateFieldActive} sx={{ mb: 3 }}>
                    <FormControl fullWidth>
                        <FormControlLabel
                            control={
                                <Switch
                                    className={LUR.inputUpdateActive}
                                    data-is-active={formData.isActive}
                                    checked={formData.isActive}
                                    onChange={handleSwitchChange}
                                    name="isActive"
                                    color="primary"
                                />
                            }
                            label={
                                <Typography sx={{ fontWeight: 600, color: '#374151' }}>
                                    {t('general.active') as string}
                                </Typography>
                            }
                        />
                    </FormControl>
                </Box>

                <Box className={LUR.updateActions}>
                    <SCUIButton
                        className={LUR.btnUpdateSubmit}
                        type={'primary'}
                        sizeType={'fixed'}
                        size={'m'}
                        onClick={() => handleSubmitButton()}
                        text={t('general.update')}
                    />
                </Box>
            </Paper>
        </Box>
    );
};

export default UpdateUserComponent;
